// Copyright 2017 Sorama B.V.

import { CaptureDevice, FrontEnd, Point3f } from "datatypes/SoramaLocalApi";
import { IGenericRepository } from "repositories/IGenericRepository";
import { ChannelSelection } from "services/ChannelSelection/ChannelSelection";
import { MeasurementDevice } from "services/Hardware/Datatypes/MeasurementDevice";
import { IHardwareService } from "services/Hardware/IHardwareService";

export class MeasurementDeviceStub {
    public id: string;
    public notes: string;

    public channelSelection: ChannelSelection;
    public position: Point3f;
    public facingDirection: Point3f;
    public upDirection: Point3f;

    public static fromMeasurementDevice(
        device: MeasurementDevice,
        channelSelection: ChannelSelection
    ): MeasurementDeviceStub {
        return <MeasurementDeviceStub>{
            id: device.id,
            notes: "",
            position: MeasurementDeviceSetupInfo.DefaultPosition,
            facingDirection: MeasurementDeviceSetupInfo.DefaultFacingDirection,
            upDirection: MeasurementDeviceSetupInfo.DefaultUpDirection,
            channelSelection: channelSelection,
        };
    }
}

// Serialized representation of ChannelSelection for storage in MeasurementDeviceSetupStub
export interface SerializedChannelSelection {
    id?: string;
    name?: string;
    singleSelection?: boolean;
    _selected?: boolean[];
}

export class MeasurementDeviceSetupStub {
    constructor(name: string, notes?: string) {
        this.name = name;
        this.notes = notes;

        this.measurementDevices = [];
    }

    public id: string;
    public userId: string;
    public name: string;
    public notes: string;

    public measurementDevices: MeasurementDeviceStub[];
}

export class MeasurementDeviceSetupInfo {
    public static DefaultPosition = <Point3f>{ X: 0, Y: 0, Z: 0 };
    public static DefaultFacingDirection = <Point3f>{ X: 0, Y: 0, Z: 1 };
    public static DefaultUpDirection = <Point3f>{ X: 0, Y: 1, Z: 0 };

    //#region IIdentifyable, INamed, INoteable Members
    public id: string;

    public get name(): string {
        return this.measurementDevice && this.measurementDevice.name
            ? this.measurementDevice.name
            : "Empty Measurement Device";
    }

    public notes: string;
    //#endregion

    // Measurement Device
    public measurementDevice: MeasurementDevice;

    // Channel Selection for this Measurement Device
    public channelSelection: ChannelSelection;

    // Position of this Measurement Device
    public position: Point3f = MeasurementDeviceSetupInfo.DefaultPosition;

    // Direction the Measurement Device is facing
    public facingDirection: Point3f = MeasurementDeviceSetupInfo.DefaultFacingDirection;

    // Direction which is upwards for the Measurement Device
    public upDirection: Point3f = MeasurementDeviceSetupInfo.DefaultUpDirection;

    // Sensor Positions of Measurement Device given current position and orientation
    public get sensorPositions(): Point3f[] {
        if (!this.measurementDevice.frontEnd)
            throw new Error("Can't get sensor positions of Measurement Device without FrontEnd.");

        if (
            this.facingDirection.X !== MeasurementDeviceSetupInfo.DefaultFacingDirection.X ||
            this.facingDirection.Y !== MeasurementDeviceSetupInfo.DefaultFacingDirection.Y ||
            this.facingDirection.Z !== MeasurementDeviceSetupInfo.DefaultFacingDirection.Z ||
            this.upDirection.X !== MeasurementDeviceSetupInfo.DefaultUpDirection.X ||
            this.upDirection.Y !== MeasurementDeviceSetupInfo.DefaultUpDirection.Y ||
            this.upDirection.Z !== MeasurementDeviceSetupInfo.DefaultUpDirection.Z
        )
            throw new Error("Non-default measurement device orientations are not supported yet.");

        return this.measurementDevice.frontEnd.microphonePositions.map(
            (pos) =>
                <Point3f>{
                    X: pos.X + this.position.X,
                    Y: pos.Y + this.position.Y,
                    Z: pos.Z + this.position.Z,
                }
        );
    }

    // Active (based on channelSelection) Sensor Positions of Measurement Device given current position and orientation
    public get activeSensorPositions(): Point3f[] {
        return this.sensorPositions.filter((x, i) => this.channelSelection.selected(i));
    }

    // Gets the frontEnd of this MeasurementDeviceSetupInfo's MeasurementDevice such that
    // its microphonePositions are set as they are in the setup.
    public get frontEndAsInSetup(): FrontEnd {
        if (!this.measurementDevice || !this.measurementDevice.frontEnd) return null;

        // Copy this so we don't change the values of the underlying reference
        const frontEnd = MeasurementDeviceSetupInfo.copy(this).measurementDevice.frontEnd;
        frontEnd.microphonePositions = this.sensorPositions;
        return frontEnd;
    }

    // Gets the captureDevices of this MeasurementDeviceSetupInfo's MeasurementDevice such that
    // its positions are set as they are in the setup.
    public get captureDevicesAsInSetup(): CaptureDevice[] {
        if (!this.measurementDevice || !this.measurementDevice.captureDevices) return null;

        // Copy this so we don't change the values of the underlying reference
        const captureDevices = MeasurementDeviceSetupInfo.copy(this).measurementDevice.captureDevices;
        return captureDevices.map((captureDevice) => {
            captureDevice.position.X += captureDevice.position.X;
            captureDevice.position.Y += captureDevice.position.Y;
            captureDevice.position.Z += captureDevice.position.Z;
            return captureDevice;
        });
    }

    constructor(
        id: string,
        measurementDevice: MeasurementDevice,
        channelSelection?: ChannelSelection,
        position?: Point3f,
        facingDirection?: Point3f,
        upDirection?: Point3f
    ) {
        this.id = id;
        this.measurementDevice = measurementDevice;
        this.channelSelection = channelSelection;

        if (position) this.position = position;

        if (facingDirection) this.facingDirection = facingDirection;

        if (upDirection) this.upDirection = upDirection;
    }

    public static copy(obj: MeasurementDeviceSetupInfo): MeasurementDeviceSetupInfo {
        if (!obj) return obj;

        const instantiatedMeasurementDeviceSetup = new MeasurementDeviceSetupInfo(
            obj.id,
            MeasurementDevice.copy(obj.measurementDevice),
            ChannelSelection.copy(obj.channelSelection),
            obj.position,
            obj.facingDirection,
            obj.upDirection
        );

        instantiatedMeasurementDeviceSetup.notes = obj.notes;

        return instantiatedMeasurementDeviceSetup;
    }
}

/**
 * Defines a setup of one or more MeasurementDevices used to perform a measurement.
 */
export class MeasurementDeviceSetup extends Array<MeasurementDeviceSetupInfo> {
    //#region IIndexableObject, INamed Members
    public id: string;

    public userId: string;

    public name: string;

    public notes: string;
    //#endregion

    //#region Properties
    public get frontEnds(): FrontEnd[] {
        return this.filter((x) => x.measurementDevice).reduce<FrontEnd[]>(
            (prev, curr) => prev.concat(curr.measurementDevice.frontEnd ? [curr.measurementDevice.frontEnd] : []),
            []
        );
    }

    public get frontEndsAsInSetup(): FrontEnd[] {
        return this.filter((x) => x.measurementDevice).reduce<FrontEnd[]>(
            (prev, curr) => prev.concat(curr.frontEndAsInSetup ? [curr.frontEndAsInSetup] : []),
            []
        );
    }

    public get captureDevices(): CaptureDevice[] {
        return this.filter((x) => x.measurementDevice).reduce<CaptureDevice[]>(
            (prev, curr) =>
                prev.concat(curr.measurementDevice.captureDevices ? curr.measurementDevice.captureDevices : []),
            []
        );
    }

    public get captureDevicesAsInSetup(): CaptureDevice[] {
        return this.filter((x) => x.measurementDevice).reduce<CaptureDevice[]>(
            (prev, curr) => prev.concat(curr.captureDevicesAsInSetup ? curr.captureDevicesAsInSetup : []),
            []
        );
    }

    public get channelSelection(): ChannelSelection {
        return this.some((x) => !x.channelSelection)
            ? undefined
            : new ChannelSelection(
                  this.reduce<boolean[]>((prev, curr) => prev.concat(curr.channelSelection.getSelection()), [])
              );
    }

    public get sensorPositions(): Point3f[] {
        return this.reduce<Point3f[]>((prev, curr) => prev.concat(curr.sensorPositions), []);
    }

    public get activeSensorPositions(): Point3f[] {
        return this.reduce<Point3f[]>((prev, curr) => prev.concat(curr.activeSensorPositions), []);
    }

    public get connectionName(): string {
        return this.frontEnds && this.frontEnds.length > 0
            ? this.frontEnds.length > 1
                ? this.name
                : this.frontEnds[0].name
            : this.name;
    }
    //#endregion

    // See: https://github.com/Microsoft/TypeScript/wiki/FAQ#why-doesnt-extending-built-ins-like-error-array-and-map-work
    constructor(...items: MeasurementDeviceSetupInfo[]) {
        const array = super(...items);
        Object.setPrototypeOf(array, MeasurementDeviceSetup.prototype);
        return array as unknown as MeasurementDeviceSetup;
    }

    public static copy(obj: MeasurementDeviceSetup): MeasurementDeviceSetup {
        if (!obj) return obj;

        const instantiatedMeasurementDeviceSetup = new MeasurementDeviceSetup(
            ...obj.map(MeasurementDeviceSetupInfo.copy)
        );
        instantiatedMeasurementDeviceSetup.id = obj.id;
        instantiatedMeasurementDeviceSetup.userId = obj.userId;
        instantiatedMeasurementDeviceSetup.name = obj.name;
        instantiatedMeasurementDeviceSetup.notes = obj.notes;

        return instantiatedMeasurementDeviceSetup;
    }

    /**
     * Create a MeasurementDeviceSetup from repository storage, NOTE: this measurement setup cannot be used for measurements, use ConnectedMeasurementDeviceSetup for this instead
     * @param stub Stub to convert to MeasurementDeviceSetup
     * @param repository Storage to retrieve MeasurementDevices from
     */
    public static fromRepository(
        stub: MeasurementDeviceSetupStub,
        repository: IGenericRepository<MeasurementDevice, MeasurementDevice>
    ): Promise<MeasurementDeviceSetup> {
        // Find all measurement devices in repositories
        const promises = stub.measurementDevices.map((x) => {
            return repository.getById(x.id).then((measurementDevice) => {
                if (!measurementDevice) throw Error("Device with id '" + x.id + "' was not found");

                const channelSelection = x.channelSelection ? ChannelSelection.copy(x.channelSelection) : undefined;

                const setupInfo = new MeasurementDeviceSetupInfo(
                    x.id,
                    measurementDevice,
                    channelSelection,
                    x.position,
                    x.facingDirection,
                    x.upDirection
                );
                setupInfo.notes = x.notes;

                return setupInfo;
            });
        });

        return Promise.all(promises).then((setupInfos) => {
            // Get all measurement devices
            const measurementSetup = new MeasurementDeviceSetup();

            for (const setupInfo of setupInfos) {
                measurementSetup.push(setupInfo);
            }

            measurementSetup.id = stub.id;
            measurementSetup.name = stub.name;
            measurementSetup.notes = stub.notes;

            return measurementSetup;
        });
    }

    /**
     * Converts this MeasurementDeviceSetup (or ConnectedMeasurementDeviceSetup) to a stub type that can be saved in a repository
     * @param setup Setup to convert to stub
     */
    public static toStub(setup: MeasurementDeviceSetup): MeasurementDeviceSetupStub {
        return <MeasurementDeviceSetupStub>{
            id: setup.id,
            name: setup.name,
            notes: setup.notes,
            measurementDevices: setup.map((x) => {
                return <MeasurementDeviceStub>{
                    id: x.id,
                    notes: x.notes,
                    channelSelection: MeasurementDeviceSetup.serializeChannelSelection(x.channelSelection),
                    facingDirection: x.facingDirection
                        ? { X: x.facingDirection.X, Y: x.facingDirection.Y, Z: x.facingDirection.Z }
                        : MeasurementDeviceSetupInfo.DefaultFacingDirection,
                    upDirection: x.upDirection
                        ? { X: x.upDirection.X, Y: x.upDirection.Y, Z: x.upDirection.Z }
                        : MeasurementDeviceSetupInfo.DefaultUpDirection,
                    position: x.position
                        ? { X: x.position.X, Y: x.position.Y, Z: x.position.Z }
                        : MeasurementDeviceSetupInfo.DefaultPosition,
                };
            }),
        };
    }

    private static serializeChannelSelection(channelSelection: ChannelSelection): SerializedChannelSelection {
        if (!channelSelection) return null;

        return {
            name: channelSelection.name,
            singleSelection: !!channelSelection.singleSelection,
            // ensure we store primitive array of booleans
            _selected: channelSelection.getSelection ? channelSelection.getSelection() : [],
            id: channelSelection.id,
        } as SerializedChannelSelection;
    }
}

export class ConnectedMeasurementDeviceSetup extends MeasurementDeviceSetup {
    // Empty variable to make sure Typescript doesnt silently cast to Connected
    private connected = true;

    public static copy(obj: ConnectedMeasurementDeviceSetup): ConnectedMeasurementDeviceSetup {
        if (!obj) return obj;

        const instantiatedMeasurementDeviceSetup = new MeasurementDeviceSetup(
            ...obj.map(MeasurementDeviceSetupInfo.copy)
        );
        instantiatedMeasurementDeviceSetup.id = obj.id;
        instantiatedMeasurementDeviceSetup.userId = obj.userId;
        instantiatedMeasurementDeviceSetup.name = obj.name;
        instantiatedMeasurementDeviceSetup.notes = obj.notes;

        return <ConnectedMeasurementDeviceSetup>instantiatedMeasurementDeviceSetup;
    }

    /**
     * Create a ConnectedMeasurementDeviceSetup from hardware service
     * @param stub Stub to convert to MeasurementDeviceSetup
     * @param measurementDeviceService Hardware service to retrieve MeasurementDevices from
     */
    public static fromConnectedDevices(
        stub: MeasurementDeviceSetupStub,
        measurementDeviceService: IHardwareService<MeasurementDevice>
    ): Promise<ConnectedMeasurementDeviceSetup> {
        // Find all measurement devices in repositories
        const promises = stub.measurementDevices.map((x) => {
            return measurementDeviceService.getHardwareById(x.id).then((measurementDevice) => {
                if (!measurementDevice) throw Error("Device with id '" + x.id + "' was not found");

                const setupInfo = new MeasurementDeviceSetupInfo(
                    x.id,
                    measurementDevice,
                    x.channelSelection,
                    x.position,
                    x.facingDirection,
                    x.upDirection
                );
                setupInfo.notes = x.notes;

                return setupInfo;
            });
        });

        return Promise.all(promises).then((setupInfos) => {
            // Get all measurement devices
            const measurementSetup = new ConnectedMeasurementDeviceSetup();

            for (const setupInfo of setupInfos) {
                measurementSetup.push(setupInfo);
            }

            measurementSetup.id = stub.id;
            measurementSetup.name = stub.name;
            measurementSetup.notes = stub.notes;

            return measurementSetup;
        });
    }

    public static fromDevice(
        device: MeasurementDevice,
        channelSelection: ChannelSelection
    ): ConnectedMeasurementDeviceSetup {
        const measurementDeviceSetup = new MeasurementDeviceSetup(
            new MeasurementDeviceSetupInfo(device.id, device, channelSelection)
        );
        measurementDeviceSetup.id = device.id;
        return <ConnectedMeasurementDeviceSetup>measurementDeviceSetup;
    }

    public static equals(a: ConnectedMeasurementDeviceSetup, b: ConnectedMeasurementDeviceSetup) {
        return (
            a === b ||
            (!!a &&
                !!b &&
                a.id === b.id &&
                a.captureDevices.every((x) => b.captureDevices.some((y) => x.id === y.id)) &&
                b.captureDevices.every((x) => a.captureDevices.some((y) => x.id === y.id)))
        );
    }
}
